import Icon from '@/components/Icon';
import { Box, Popover } from '@mui/material';
import { flexStyle, textStyle } from '@themes/mixins';
import styled, { css } from 'styled-components';

/* Root Container */
export const Root = styled.div``;

/* Popover styled for theme consistency */
export const StyledPopover = styled(Popover)`
  & .MuiPaper-root {
    background-color: ${({ theme }) => theme.colors.background};
    border: 1px solid ${({ theme }) => theme.colors.divider};
    border-radius: 10px;
    padding: 0;
    min-width: 360px;
    box-shadow: ${({ theme }) =>
      theme.shadows?.[4] || '0px 4px 12px rgba(0,0,0,0.1)'};
  }
`;

/* Top bar icon */
export const PropertiesIcon = styled(Icon)<{ open?: boolean }>`
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 6px;
  border-radius: 8px;
  transition: all 0.25s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceSecondary};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  ${({ open, theme }) =>
    open &&
    css`
      color: ${theme.colors.textPrimary};
    `}
`;

/* Wrapper for button in navbar */
export const ButtonWrapper = styled.div<{ open?: boolean }>`
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: background-color 0.2s ease-in-out;

  ${({ open, theme }) =>
    open &&
    css`
      background-color: ${theme.colors.surfaceSecondary};
    `}

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

/* Popover content layout */
export const ContentContainer = styled(Box)`
  ${flexStyle({ direction: 'column' })};
  gap: 22px;
  padding: 16px;
`;

/* Section wrapper */
export const Section = styled.div`
  ${flexStyle({ direction: 'column' })};
  gap: 8px;
`;

/* Section header */
export const SectionHeader = styled.div`
  ${flexStyle({ align: 'center', justify: 'flex-start', direction: 'row' })};
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1,
    size: '12px',
    weight: 600,
    textAlign: 'left',
  })};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
  gap: 10px;
  padding: 6px;
  padding-bottom: 20px;
  svg {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

/* Input and icons aligned horizontally */
export const InlineRow = styled.div`
  ${flexStyle({ align: 'center', justify: 'flex-start', direction: 'row' })};
  width: 100%;
  gap: 6px;

  & > div:first-child {
    flex: 1;
    min-width: 0;
  }
`;

/* Divider line */
export const Divider = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  width: 100%;
`;

export const StyledIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* Action icon buttons */
export const IconButton = styled.div<{ $disabled?: boolean }>`
  ${flexStyle({ align: 'center', justify: 'center' })};
  width: 30px;
  height: 30px;
  min-width: 30px;
  border-radius: 6px;
  transition: all 0.2s ease-in-out;
  background-color: ${({ theme }) => theme.colors.secondaryBackground};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  flex-shrink: 0;

  & ${StyledIcon} {
    transition: color 0.2s ease;
  }

  &:hover {
    ${({ $disabled, theme }) =>
      !$disabled &&
      css`
        background-color: ${theme.colors.primary};
      `}

    & ${StyledIcon} {
      ${({ $disabled, theme }) =>
        !$disabled &&
        css`
          color: ${theme.colors.white};
        `}
    }
  }
`;

/* Styled Autocomplete wrapper for dark mode support */
export const StyledAutocompleteWrapper = styled.div`
  flex: 1;

  .MuiAutocomplete-root {
    .MuiOutlinedInput-root {
      background-color: ${({ theme }) => theme.colors.background};
      color: ${({ theme }) => theme.colors.textPrimary};

      .MuiOutlinedInput-notchedOutline {
        border-color: ${({ theme }) => theme.colors.divider};
      }

      &:hover .MuiOutlinedInput-notchedOutline {
        border-color: ${({ theme }) => theme.colors.textSecondary};
      }

      &.Mui-focused .MuiOutlinedInput-notchedOutline {
        border-color: ${({ theme }) => theme.colors.primary};
      }
    }

    .MuiInputLabel-root {
      color: ${({ theme }) => theme.colors.textSecondary};

      &.Mui-focused {
        color: ${({ theme }) => theme.colors.primary};
      }
    }

    .MuiAutocomplete-input {
      color: ${({ theme }) => theme.colors.textPrimary};

      &::placeholder {
        color: ${({ theme }) => theme.colors.textSecondary};
        opacity: 0.6;
      }
    }

    .MuiAutocomplete-clearIndicator,
    .MuiAutocomplete-popupIndicator {
      color: ${({ theme }) => theme.colors.textSecondary};
    }
  }

  .MuiAutocomplete-popper {
    .MuiPaper-root {
      background-color: ${({ theme }) => theme.colors.background};
      border: 1px solid ${({ theme }) => theme.colors.divider};

      .MuiAutocomplete-option {
        color: ${({ theme }) => theme.colors.textPrimary};

        &:hover {
          background-color: ${({ theme }) => theme.colors.surfaceSecondary};
        }

        &[aria-selected='true'] {
          background-color: ${({ theme }) => theme.colors.surfaceSecondary};
        }
      }

      .MuiAutocomplete-noOptions {
        color: ${({ theme }) => theme.colors.textSecondary};
      }
    }
  }
`;
